<template>
  <ValidationObserver ref="form">
    <custom-label v-if="label" :item-field-name="label" />
    <div class="d-flex flex-wrap flex-gap-xs">
      <!-- Country Code Dropdown -->
      <ValidationProvider
        v-slot="{ errors }"
        name="country code"
        :rules="{ required: true }"
        style="width: 2.5rem; min-width: 120px"
      >
        <v-autocomplete
          v-model="countryCode"
          hide-details="auto"
          dense
          filled
          rounded
          :background-color="backgroundColor || $vuetify.theme.themes.light.inputBackgroundColor"
          :menu-props="{ offsetY: true }"
          :items="countryCallingCodes"
          :class="{ 'small-input': small }"
          :error="!!errors.length"
          :error-messages="errors"
          :disabled="disabled"
        ></v-autocomplete>
      </ValidationProvider>

      <!-- Phone Number Field -->
      <ValidationProvider
        v-slot="{ errors }"
        name="phone"
        :rules="{ required: true, isDigit: true, validPhone: [computedFullPhone] }"
        class="flex-grow-1"
      >
        <v-text-field
          v-model="phoneNumber"
          hide-details="auto"
          filled
          rounded
          :background-color="backgroundColor || $vuetify.theme.themes.light.inputBackgroundColor"
          dense
          placeholder="Phone Number"
          :class="{ 'small-input': small }"
          :error="!!errors.length"
          :error-messages="errors"
          :disabled="disabled"
        ></v-text-field>
      </ValidationProvider>
    </div>
  </ValidationObserver>
</template>

<script>
import { ValidationObserver, ValidationProvider, Validator } from 'vee-validate';
import { parsePhoneNumber } from 'libphonenumber-js';
import { countryCallingCodes } from '@/data/optionsConstants';
import customLabel from '@/components/customFormLabel/customLabel';
const onlyDigitRegex = /^\d+$/g;

// Custom validation rules
Validator.extend('isDigit', {
  getMessage: (field, params, data) => {
    return 'Please input numbers only';
  },
  validate: value => {
    return !value || value?.match(onlyDigitRegex) !== null;
  },
});

Validator.extend('validPhone', {
  getMessage: (field, params, data) => {
    return 'Phone number is not valid';
  },
  validate: (value, [fullPhone]) => {
    if (!value) return false;
    try {
      const phoneNumber = parsePhoneNumber(fullPhone);
      if (phoneNumber?.country === 'HK') {
        if (phoneNumber.nationalNumber?.length === 8) {
          return true;
        }
      }
      return phoneNumber && phoneNumber?.isValid();
    } catch (error) {
      return false;
    }
  },
});

export default {
  name: 'FletrixPhone',
  components: { ValidationObserver, ValidationProvider, customLabel },
  props: {
    value: { type: String, default: '' },
    defaultCountryCode: { type: String, default: '+852' },
    small: { type: Boolean, default: false },
    label: { type: String, default: '' },
    backgroundColor: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      countryCallingCodes,
      countryCode: this.defaultCountryCode,
      phoneNumber: '',
    };
  },
  computed: {
    computedFullPhone() {
      if (!this.countryCode || !this.phoneNumber) return '';
      return this.countryCode + this.phoneNumber;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        if (v) {
          try {
            const parsed = parsePhoneNumber(v);
            if (parsed) {
              this.countryCode = '+' + parsed.countryCallingCode;
              this.phoneNumber = parsed.nationalNumber;
            }
          } catch (error) {
            // If parsing fails, try to extract manually
            const match = v.match(/^(\+\d{1,4})(.*)$/);
            if (match) {
              this.countryCode = match[1];
              this.phoneNumber = match[2];
            } else {
              this.countryCode = this.defaultCountryCode;
              this.phoneNumber = v;
            }
          }
        } else {
          this.countryCode = this.defaultCountryCode;
          this.phoneNumber = '';
        }
      },
    },
    computedFullPhone: {
      handler(newPhone) {
        if (newPhone && newPhone !== this.value) {
          this.$emit('input', newPhone);
        } else if (!newPhone && this.value) {
          this.$emit('input', '');
        }
      },
      immediate: true,
    },
  },
  methods: {
    reset() {
      this.countryCode = this.defaultCountryCode;
      this.phoneNumber = '';
      this.$refs.form?.reset();
    },
    validate() {
      return this.$refs.form?.validate();
    },
  },
};
</script>

<style lang="scss" scoped>
.flex-gap-xs > * {
  margin-right: 8px;
}

.flex-gap-xs > *:last-child {
  margin-right: 0;
}

.small-input {
  font-size: 0.875rem;
}
</style>
